import React, { useState } from 'react';
import { Upload, Button, message, Table, Card, Input, Form, Space, Descriptions, Tabs, Collapse, Empty, Switch, InputNumber, Radio } from 'antd';
import { UploadOutlined, SearchOutlined, PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import axios from 'axios';
import './index.less';

const { TabPane } = Tabs;
const { Panel } = Collapse;

interface OrderData {
  key: string;
  store: string;
  orderNumber: string;
  orderTime: string;
  status: string;
  trackingNumber: string;
}

interface ActivationData {
  id: number;
  token: string;
  deviceInfo: any;
  activatedAt: string;
  firstActivatedAt?: string;
  browserInfo?: any;
  osInfo?: any;
  ipAddress?: string;
  userAgent?: string;
  lastActiveAt?: string;
  accessCount?: number;
}

interface DeviceData {
  id: number;
  name: string;
  browserInfo: any;
  osInfo: any;
  ipAddress: string;
  userAgent: string;
  lastActiveAt: string;
}

interface OrderDetail {
  id: number;
  store: string;
  orderNumber: string;
  orderTime: string;
  status: string;
  trackingNumber: string;
  maxActivations: number;
  isDisabled: boolean;
  createdAt: string;
  updatedAt: string;
  activations: ActivationData[];
}

// 本地定义所需字段，避免依赖后端实体文件
interface IncrediboxOc {
  bpm: number;
  hashValue: string;
  link: string;
  topColor: string;
  bottomColor: string;
}

const RhythmBoxModule: React.FC = () => {
  const [orderData, setOrderData] = useState<OrderData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [orderDetail, setOrderDetail] = useState<OrderDetail | null>(null);
  const [generatingCode, setGeneratingCode] = useState<boolean>(false);
  const [maxActivationCount, setMaxActivationCount] = useState<number>(16); // 默认最大激活数
  const [form] = Form.useForm();
  const [ocForm] = Form.useForm(); // 新增 OC 卡生成模块的表单实例
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);

  const columns = [
    {
      title: '店铺',
      dataIndex: 'store',
      key: 'store',
    },
    {
      title: '订单号码',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
    },
    {
      title: '下单时间',
      dataIndex: 'orderTime',
      key: 'orderTime',
    },
    {
      title: '交易状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const validStatuses = ['已发货', '交易成功', '自主生成'];
        const isValid = validStatuses.includes(status);
        return (
          <span style={{ 
            color: isValid ? '#52c41a' : '#ff4d4f',
            fontWeight: 'bold'
          }}>
            {status}
          </span>
        );
      }
    },
    {
      title: '物流单号',
      dataIndex: 'trackingNumber',
      key: 'trackingNumber',
    },
  ];

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        // 读取第3行的表头，动态定位各字段所在列
        const range = XLSX.utils.decode_range(worksheet['!ref'] as string);
        const HEADER_ROW_INDEX = 3; // 以人类可读的第3行为表头

        const findColumnByHeader = (headerText: string): string | undefined => {
          for (let c = range.s.c; c <= range.e.c; c++) {
            const cellAddress = XLSX.utils.encode_cell({ c, r: HEADER_ROW_INDEX - 1 });
            const cell = (worksheet as any)[cellAddress];
            if (cell && typeof cell.v !== 'undefined') {
              const cellValue = String(cell.v).trim();
              if (cellValue === headerText) {
                return XLSX.utils.encode_col(c); // 返回列字母，如 'A','B','C'
              }
            }
          }
          return undefined;
        };

        // 根据表头动态映射列，未找到时回退到既有默认列
        const columnMap = {
          store: findColumnByHeader('网店') || 'C',
          orderNumber: findColumnByHeader('订单编号') || 'D',
          orderTime: findColumnByHeader('付款时间') || 'E',
          status: findColumnByHeader('线上交易状态') || 'L',
          trackingNumber: findColumnByHeader('物流单号') || 'R',
        };

        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 'A' });
        
        let validOrders: OrderData[] = [];
        let orderIndex = 0;
        
        // 过滤并提取需要的列数据
        jsonData
          .filter((row: any) => {
            // 过滤掉A列不是数字的行（可能是标题行或无效数据）
            return row['A'] && !isNaN(Number(row['A']));
          })
          .forEach((row: any) => {
            // 处理订单号中可能存在的分号分隔符
            const orderNumberStr = row[columnMap.orderNumber] || '';
            const orderNumbers = orderNumberStr.split(';').filter(Boolean);
            
            // 如果有多个订单号，为每个订单号创建一条记录
            orderNumbers.forEach((orderNumber: string) => {
              validOrders.push({
                key: (orderIndex++).toString(),
                store: row[columnMap.store] || '',
                orderNumber: orderNumber.trim(),
                orderTime: row[columnMap.orderTime] || '',
                status: row[columnMap.status] || '',
                trackingNumber: row[columnMap.trackingNumber] || '',
              });
            });
          });

        setOrderData(validOrders);
        message.success(`成功导入${validOrders.length}条订单数据`);
      } catch (error) {
        console.error('解析文件时出错:', error);
        message.error('解析表格文件失败，请检查文件格式');
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // 阻止自动上传
  };

  // 分批上传数据，避免请求体过大
  const uploadToServer = async () => {
    if (orderData.length === 0) {
      message.warning('没有可上传的订单数据');
      return;
    }

    setLoading(true);
    try {
      // 如果数据量很大，分批上传
      const BATCH_SIZE = 500; // 增大每批数据量至500条
      let successCount = 0;
      let updatedCount = 0;
      let createdCount = 0;
      
      if (orderData.length <= BATCH_SIZE) {
        // 数据量较小，直接上传
        const response = await axios.post('/api/v1/rhythm_box_module/upload_orders', orderData);
        if (response.data.success) {
          successCount = response.data.savedCount;
          updatedCount = response.data.updatedCount;
          createdCount = response.data.createdCount;
        } else {
          throw new Error(response.data.message);
        }
      } else {
        // 数据量较大，分批上传
        for (let i = 0; i < orderData.length; i += BATCH_SIZE) {
          const batch = orderData.slice(i, i + BATCH_SIZE);
          const response = await axios.post('/api/v1/rhythm_box_module/upload_orders', batch);
          
          if (response.data.success) {
            successCount += response.data.savedCount;
            updatedCount += response.data.updatedCount;
            createdCount += response.data.createdCount;
            // 更新进度
            message.info(`已处理 ${i + batch.length}/${orderData.length} 条记录...`);
          } else {
            throw new Error(`第${i/BATCH_SIZE + 1}批数据上传失败: ${response.data.message}`);
          }
        }
      }
      
      message.success(`成功保存订单数据，共${successCount}条记录 (更新${updatedCount}条，新建${createdCount}条)`);
    } catch (error: any) {
      console.error('上传订单数据时出错:', error);
      message.error(`上传订单数据失败: ${error.message || '未知错误'}`);
    } finally {
      setLoading(false);
    }
  };

  // 查询订单
  const searchOrder = async (values: { searchTerm: string }) => {
    if (!values.searchTerm) {
      message.warning('请输入查询内容');
      return;
    }

    setSearchLoading(true);
    try {
      // 使用新的搜索API
      const response = await axios.get(`/api/v1/rhythm_box_module/orders/search/${encodeURIComponent(values.searchTerm)}`);
      
      if (response.data.success && response.data.count > 0) {
        // 更新表格数据显示搜索结果
        const searchResults = response.data.data.map((item: any, index: number) => ({
          ...item,
          key: item.id || index.toString(),
        }));
        setOrderData(searchResults);
        message.success(`找到${response.data.count}条记录`);
        
        // 如果只有一条数据，自动加载详情
        if (response.data.count === 1) {
          setOrderDetail(response.data.data[0]);
        } else {
          setOrderDetail(null);
        }
      } else {
        message.warning('未找到匹配的订单信息');
        setOrderDetail(null);
      }
    } catch (error: any) {
      console.error('搜索订单信息时出错:', error);
      message.error(`搜索订单信息失败: ${error.message || '未知错误'}`);
      setOrderDetail(null);
    } finally {
      setSearchLoading(false);
    }
  };

  // 生成随机6位数字激活码
  const generateRandomCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // 创建新的激活码
  const createActivationCode = async () => {
    setGeneratingCode(true);
    try {
      // 生成随机6位数字激活码
      const activationCode = generateRandomCode();
      
      // 确保最大激活数为有效值
      const maxActivations = maxActivationCount && maxActivationCount > 0 ? maxActivationCount : 16;
      
      // 准备创建的订单数据
      const newOrder = {
        store: '自主生成',
        orderNumber: activationCode,
        orderTime: new Date().toISOString(),
        status: '自主生成',
        trackingNumber: '',
        maxActivations: maxActivations, // 使用当前状态中的值
      };
      
      console.log('创建激活码请求数据:', newOrder); // 添加日志方便调试
      
      // 发送到服务器并特别检查传递的maxActivations值
      console.log('发送的maxActivations值:', maxActivations);
      console.log('发送的JSON数据:', JSON.stringify([newOrder]));
      
      const response = await axios.post('/api/v1/rhythm_box_module/upload_orders', [newOrder]);
      
      console.log('服务器响应:', response.data);
      
      if (response.data.success) {
        message.success(`成功生成激活码: ${activationCode} (最大激活数: ${maxActivations})`);
        
        // 将激活码填入查询框
        form.setFieldsValue({ searchTerm: activationCode });
        
        // 查询新创建的激活码详情
        searchOrder({ searchTerm: activationCode });
      } else {
        throw new Error(response.data.message);
      }
    } catch (error: any) {
      console.error('生成激活码时出错:', error);
      message.error(`生成激活码失败: ${error.message || '未知错误'}`);
    } finally {
      setGeneratingCode(false);
    }
  };

  // 格式化日期时间
  const formatDateTime = (dateTimeStr: string) => {
    if (!dateTimeStr) return '-';
    const date = new Date(dateTimeStr);
    return date.toLocaleString();
  };

  // 切换订单禁用状态
  const toggleOrderStatus = async (id: number, currentStatus: boolean) => {
    setUpdateLoading(true);
    try {
      const response = await axios.put(`/api/v1/rhythm_box_module/orders/${id}/toggle_status`);
      
      if (response.data.success) {
        message.success(response.data.message);
        
        // 更新本地订单数据
        if (orderDetail && orderDetail.id === id) {
          setOrderDetail({
            ...orderDetail,
            isDisabled: !currentStatus
          });
        }
      } else {
        message.error(response.data.message || '切换状态失败');
      }
    } catch (error: any) {
      console.error('更新订单状态时出错:', error);
      message.error(`更新订单状态失败: ${error.message || '未知错误'}`);
    } finally {
      setUpdateLoading(false);
    }
  };

  // 更新最大激活次数
  const updateMaxActivations = async (id: number, maxActivations: number) => {
    if (isNaN(maxActivations) || maxActivations < 1) {
      message.error('最大激活次数必须是大于0的整数');
      return;
    }

    setUpdateLoading(true);
    try {
      const response = await axios.put(`/api/v1/rhythm_box_module/orders/${id}/max_activations`, {
        maxActivations
      });
      
      if (response.data.success) {
        message.success(response.data.message);
        
        // 更新本地订单数据
        if (orderDetail && orderDetail.id === id) {
          setOrderDetail({
            ...orderDetail,
            maxActivations
          });
        }
      } else {
        message.error(response.data.message || '更新最大激活次数失败');
      }
    } catch (error: any) {
      console.error('更新最大激活次数时出错:', error);
      message.error(`更新最大激活次数失败: ${error.message || '未知错误'}`);
    } finally {
      setUpdateLoading(false);
    }
  };

  // 渲染订单详情
  const renderOrderDetail = () => {
    if (!orderDetail) return <Empty description="暂无订单信息" />;

    // 创建激活信息表格的列定义
    const activationColumns = [
      {
        title: '激活令牌',
        dataIndex: 'token',
        key: 'token',
        width: '20%',
      },
      {
        title: '首次激活时间',
        dataIndex: 'firstActivatedAt',
        key: 'firstActivatedAt',
        render: (text: string, record: ActivationData) => formatDateTime(text || record.activatedAt),
        width: '15%',
      },
      {
        title: '最后活动时间',
        dataIndex: 'lastActiveAt',
        key: 'lastActiveAt',
        render: (text: string) => formatDateTime(text) || '-',
        width: '15%',
      },
      {
        title: '访问次数',
        dataIndex: 'accessCount',
        key: 'accessCount',
        render: (text: number, record: ActivationData) => {
          return record.accessCount || 1;
        },
        width: '10%',
      },
      {
        title: '设备信息',
        key: 'deviceInfo',
        render: (record: ActivationData) => {
          if (!record.lastActiveAt) {
            return '未使用';
          }
          
          return (
            <div>
              <div>设备: {record.browserInfo?.name || '-'}</div>
              <div>系统: {record.osInfo?.name || '-'}</div>
              <div>IP: {record.ipAddress || '-'}</div>
              <div>UA: <div style={{ maxWidth: '100%', wordBreak: 'break-word', whiteSpace: 'normal' }}>{record.userAgent || '-'}</div></div>
            </div>
          );
        },
        width: '40%',
      }
    ];

    return (
      <div className="order-detail">
        <Descriptions title="订单基本信息" bordered>
          <Descriptions.Item label="订单号/激活码">{orderDetail.orderNumber}</Descriptions.Item>
          <Descriptions.Item label="来源">{orderDetail.store || '-'}</Descriptions.Item>
          <Descriptions.Item label="创建时间">{formatDateTime(orderDetail.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="物流单号">{orderDetail.trackingNumber || '-'}</Descriptions.Item>
          <Descriptions.Item label="订单状态">
            <span style={{ 
              color: ['已发货', '交易成功', '自主生成'].includes(orderDetail.status) ? '#52c41a' : '#ff4d4f',
              fontWeight: 'bold'
            }}>
              {orderDetail.status || '-'}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="最大激活数">
            <InputNumber 
              min={1} 
              max={100}
              value={orderDetail.maxActivations} 
              onChange={(value) => {
                // 仅在本地更新状态，不发送API请求
                if (value !== null && !isNaN(value)) {
                  setOrderDetail({
                    ...orderDetail,
                    maxActivations: value as number
                  });
                }
              }}
              onBlur={() => {
                // 在失去焦点时才发送更新请求
                if (orderDetail.maxActivations !== null && 
                    !isNaN(orderDetail.maxActivations) && 
                    orderDetail.maxActivations > 0) {
                  updateMaxActivations(orderDetail.id, orderDetail.maxActivations);
                }
              }}
              disabled={updateLoading}
            />
          </Descriptions.Item>
          <Descriptions.Item label="是否禁用">
            <Switch 
              checked={orderDetail.isDisabled} 
              onChange={() => toggleOrderStatus(orderDetail.id, orderDetail.isDisabled)}
              loading={updateLoading}
              checkedChildren="是" 
              unCheckedChildren="否"
            />
          </Descriptions.Item>
          <Descriptions.Item label="更新时间">{formatDateTime(orderDetail.updatedAt)}</Descriptions.Item>
        </Descriptions>

        <Card title="激活信息" className="activations-card" style={{ marginTop: '16px' }}>
          {orderDetail.activations && orderDetail.activations.length > 0 ? (
            <Table 
              columns={activationColumns} 
              dataSource={orderDetail.activations.map(item => ({...item, key: item.id}))} 
              pagination={false}
              size="small"
            />
          ) : (
            <Empty description="暂无激活信息" />
          )}
        </Card>
      </div>
    );
  };

  const generateRandomColor = () => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };
  
  // const generateHash = () => {
  //   // 避免容易混淆的字符: 0, o, 1, i, l
  //   const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
  //   let hash = '';
  //   for (let i = 0; i < 5; i++) {
  //     hash += chars[Math.floor(Math.random() * chars.length)];
  //   }
  //   return hash;
  // };
  
  const handleGenerate = async () => {
    try {
      const values = await ocForm.validateFields(); // 使用 ocForm 而不是 form
      const { bpmType, quantity } = values;
      
      // 生成指定数量的记录
      const records = [];
      for (let i = 0; i < quantity; i++) {
        records.push({
          bpm: bpmType,
          // hashValue: generateHash(),
          link: ``,
          topColor: generateRandomColor(),
          bottomColor: generateRandomColor()
        });
      }
      
      // 发送到后端
      const response = await axios.post('/api/v1/incredibox/oc/bulk-upload', records);
      
      if (response.data.success) {
        message.success(`成功生成 ${response.data.count} 条记录`);
        ocForm.resetFields(); // 使用 ocForm 重置表单
        console.log(response.data.data);
        // 准备Excel数据
        const excelData = response.data.data.map((record: IncrediboxOc) => ({
          'BPM': record.bpm,
          '二维码值': `https://mixbox.pixelhouse.cn/oc/${record.hashValue}`,
        }));

        console.log(excelData);

        // 创建工作簿
        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'OC卡数据');

        // 生成文件名
        const fileName = `OC卡数据_${new Date().toISOString().split('T')[0]}.xlsx`;

        // 下载文件
        XLSX.writeFile(wb, fileName);

        // 准备Excel数据
        // const excelData = records.map(record => ({
        //   'BPM': record.bpm,
        //   'Hash值': record.hashValue,
        //   '链接': record.link,
        //   '顶部颜色': record.topColor,
        //   '底部颜色': record.bottomColor
        // }));

        // // 创建工作簿
        // const ws = XLSX.utils.json_to_sheet(excelData);
        // const wb = XLSX.utils.book_new();
        // XLSX.utils.book_append_sheet(wb, ws, 'OC卡数据');

        // // 生成文件名
        // const fileName = `OC卡数据_${new Date().toISOString().split('T')[0]}.xlsx`;

        // // 下载文件
        // XLSX.writeFile(wb, fileName);
      } else {
        message.error(response.data.message || '生成失败');
      }
    } catch (error) {
      console.error('生成失败:', error);
      message.error('生成失败，请重试');
    }
  };

  return (
    <div className="rhythm-box-module-container">
      <Card title="节奏盒子模组卡订单导入" className="upload-card">
        <Upload
          accept=".xlsx,.xls,.csv"
          beforeUpload={handleFileUpload}
          showUploadList={false}
        >
          <Button icon={<UploadOutlined />}>上传订单表格</Button>
        </Upload>
        <p className="upload-tip">支持.xlsx, .xls, .csv格式的表格文件</p>
        
        {orderData.length > 0 && (
          <Button 
            type="primary" 
            onClick={uploadToServer} 
            loading={loading}
            style={{ marginTop: '10px' }}
          >
            上传到服务器
          </Button>
        )}
        
        {/* 添加订单列表预览 */}
        {orderData.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <h3>已导入订单数据 ({orderData.length}条)</h3>
            <Table 
              columns={columns} 
              dataSource={orderData} 
              pagination={{ pageSize: 10 }}
              size="small"
              rowKey="key"
            />
          </div>
        )}
      </Card>

      <Card title="订单查询与激活码生成" className="search-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <Form 
            form={form}
            layout="inline" 
            onFinish={searchOrder}
          >
            <Form.Item 
              name="searchTerm"
              rules={[{ required: true, message: '请输入查询内容' }]}
            >
              <Input 
                placeholder="输入物流单号或订单号/激活码" 
                prefix={<SearchOutlined />} 
                allowClear 
                style={{ width: 240 }}
              />
            </Form.Item>
            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={searchLoading}
              >
                查询
              </Button>
            </Form.Item>
          </Form>

          <Space>
            <InputNumber
              min={1}
              max={100}
              value={maxActivationCount}
              onChange={(value) => setMaxActivationCount(value as number)}
              placeholder="最大设备数"
              style={{ width: 120 }}
            />
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={createActivationCode}
              loading={generatingCode}
            >
              生成激活码
            </Button>
          </Space>
        </div>

        <div className="search-result">
          {renderOrderDetail()}
        </div>
      </Card>

      <Card title="生成OC卡">
        <Form form={ocForm} layout="vertical">
          <Form.Item label="BPM类型" name="bpmType" initialValue={100}>
            <Radio.Group>
              <Radio value={100}>100 BPM</Radio>
              <Radio value={120}>120 BPM</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="生成数量" name="quantity" initialValue={1}>
            <InputNumber min={1} max={10000} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={handleGenerate}>生成</Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default RhythmBoxModule; 